<template>
	<el-row>
		<el-col :span="18">
			<div class="base-form header">				
				<h2>Группы запчастей</h2>				
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddItemsGroupClick">
					Новая группа запчастей
				</el-button>
			</div>
		</el-col>
	</el-row>
	
	<el-row>
		<el-col :span="6">
			<div class="base-form filter">
				<el-input v-model="tableSearchQuery"
					@change="fetchItemsGroupsAsync"
					placeholder="Поиск групп запчастей по названию" clearable class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id"
				v-loading="tableLoading"	
				:data="tableData"			
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				:element-loading-svg="tableLoadingSvg"
				@sort-change="onTableSortingChange"
				@row-click="onTableRowClick">			
				<template #empty>
					Список групп запчастей пуст ☹️, попробуйте изменить фильтр
				</template>	
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onPickItemsGroupClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Группа" prop="name" sortable column-key="name" />
				<el-table-column label="Описание" prop="description" column-key="description" />		
				<el-table-column v-if="!isPickerMode" align="right" width="74">
					<template #default="scope">		
						<el-button 
							:icon="deleteIcon" 
							type="danger"
							circle
							@click="(e) => onTableDeleteButtonClick(scope.row.id, e)"
						/>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search, Delete } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import ItemGroupModel from '@/models/ItemGroupModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import ViewType from '@/models/enums/ViewType';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	}, 
	props: {
		params: Array
	}
})
export default class ItemsGroupsView extends Vue {
	/** params */
	params!: Array<any>;

	/** constants */	
	deleteIcon = Delete;
	circleIcon = CirclePlus;
	searchIcon = Search;
	refreshEvent = 'itemsGroupsUpdate';
	itemsGroupsHttpClient = axiosHelper.createInstance(store.getters.jwt);

	/** view-models */
	onPickedCallback: Function | null = null;
	isPickerMode = false;

	tableLoading = false;
	tableData = new Array<ItemGroupModel>();
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';

	mounted() {
		this.initView();
		EventHelper.setEventListener(this.refreshEvent, this.fetchItemsGroupsAsync);
		EventHelper.dispatchEvent(this.refreshEvent);
	}

	unmounted() {		
		EventHelper.removeEventListener(this.refreshEvent, this.fetchItemsGroupsAsync);
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback;
		}
	}

	/** fires on table's row is clicked */
	onTableRowClick(row: ItemGroupModel) {
		this.openItemsGroupViewTab(row, false);
	}

	/** fires on 'Create New Items Group' button clicked */
	onAddItemsGroupClick() {
		this.openItemsGroupViewTab(null, true);
	}

	openItemsGroupViewTab(itemsGroup: ItemGroupModel | null, creatingMode: boolean) {
		const addItemTab = TabsDictionary.itemsGroup;
		addItemTab.params = [ itemsGroup, creatingMode ? ViewType.Creating : ViewType.Editing ];
		store.commit('addTab', addItemTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	/** handles callback and passes selected component-group if picker-mode enabled */	
	onPickItemsGroupClick(component: ItemGroupModel, e: Event) {
		e.stopPropagation();
		if (!this.onPickedCallback) {
			return;
		}
		this.onPickedCallback(component);
	}

	async fetchItemsGroupsAsync() {		
		try {
			this.tableLoading = true;			
			const filterObject = {
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'name',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery
			};			
			const response = await this.itemsGroupsHttpClient.get('/api/itemsgroups', { params: filterObject });
			const groupsTree = this.mapGroupsTree(response?.data);
			this.tableData = groupsTree;
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: `Ошибка соединения, не удалось загрузить группы сервисных компонетов (узлов)`,
					type: 'error',
				});
			} else {
				ElNotification({
					message: `Ошибка загрузка групп сервисных компонетов (узлов)`,
					type: 'error',
				});
			}
			console.error('Error loading service-job-component-groups list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	onTableDeleteButtonClick(itemsGroupId: number, event: Event) {
		event.stopPropagation();

		ElMessageBox.confirm(
			'Группа запчастей будет удалена',
			{
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'error',
			}
		)
		.then(async () => {
			const jwt = store.getters.jwt;
			const a = axiosHelper.createInstance(jwt);
			try {
				this.tableLoading = true;
				await a.delete(`/api/itemsgroups/${itemsGroupId}`);
				ElNotification({
					title: 'Группа запчастей удалена',
					type: 'success',
				});	
				await this.fetchItemsGroupsAsync();
				this.tableLoading = false;
			} catch (e) {			
				ElNotification({
					title: 'Ошибка, группа запчастей не была удалена',
					type: 'error',
				});	
				console.error(`Error deleting items group #${itemsGroupId}`, e)
			} finally {
				this.tableLoading = false;
			}
		});		
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder == a.order;
		this.fetchItemsGroupsAsync();
	}

	mapGroupsTree(data: Array<any> | undefined) : Array<ItemGroupModel> {
		if (!data) {
			return [];
		}

		const map = this.mapGroupsTree;
		return data.map((g: any) => {

			const group = new ItemGroupModel(g);
			if (!g?.childGroups?.length) {
				group.children = null;
			} 
			else {
				group.children = map(g.childGroups);
			}
			
			return group;
		});
	}
}
</script>